import {Box, Flex, PositionedBox, Text, showMessage} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'

import {sessionWallClock} from '../../api/booking/helpers'
import {WeekAvailableDays} from '../../api/booking/types'
import {ClassQueries} from '../../api/class'
import {SessionSelectSession} from '../../api/class-session/types'
import {MemberQueries} from '../../api/member'
import {useVenue} from '../../api/venue/hooks'
import {InlineCalendar} from '../../components/inline-calendar/inline-calendar'
import {Slides} from '../../components/inline-calendar/types'
import {PrivateCodeModal} from '../../components/private-code-modal/private-code-modal'
import {SessionCard} from '../../components/session-card/session-card'
import {WaitingListModal} from '../../components/waiting-list-modal/waiting-list-modal'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {TranslatorScope} from '../../locale/types'
import {BookingService} from '../../services/booking'
import {checkClassPasscode, getWaitingListBookingsRequest} from '../../services/booking/actions'
import {CreateClassBookingFormType} from '../../services/booking/forms/create-class-booking-form'
import {SelectedSession} from '../../services/booking/forms/types'
import {useBookingLoader} from '../../services/booking/hooks'
import {UserService} from '../../services/user'
import {UnjoinWaitingListModal} from '../../components/waiting-list-modal/unjoin-waiting-list-modal'
import {DividerWithContent} from '../../ui/components/divider-with-content/divider-with-content'
import {ScrollableWrapper} from '../../ui/components/scrollable-wrapper/scrollable-wrapper'
import {auth} from '../../utils/firebase'
import {useLocale, useLuxon} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'

const Header: FC = () => {
  const {l} = useLocale()
  const {data} = useVenue()
  return <SimpleGridLayoutHeader title={data?.entityNames.classes || l('classes')} />
}

type WeekCalendarType = {
  today: DateTime
  selectedDate: DateTime
  setSelectedDate: (date: DateTime) => void
  startOfSelectedWeek: DateTime
  weekDaysData?: WeekAvailableDays
  setStartOfSelectedWeek: (slides: Slides) => void
}

const weekDaysNumbers = [0, 1, 2, 3, 4, 5, 6]
const defaultWeekDays = [false, false, false, false, false, false, false]

export const messagesByReason: (
  key?: SessionSelectSession['permissionData']['reason'],
) => TranslatorScope | undefined = (key) => {
  if (!key) {
    return
  }
  return {
    NOT_AVAILABLE: 'entity_is_not_available',
    FOR_MEMBERS_ONLY: 'for_members_only',
    CLASS_IS_NOT_FOUND: 'something_went_wrong',
    FOR_SUBSCRIBERS_ONLY: 'only_with_subscription',
    ONLY_WITHOUT_SUBSCRIPION: 'only_without_subscription',
    SLOT_IS_BOOKED_BY_THIS_MEMBER: 'is_already_booked',
    FOR_NON_MEMBERS_ONLY: 'for_non_members_only',
  }[key] as TranslatorScope
}

export const WeekCalendar: FC<WeekCalendarType> = ({
  today,
  weekDaysData,
  selectedDate,
  setSelectedDate,
  startOfSelectedWeek,
  setStartOfSelectedWeek,
}) => {
  const luxon = useLuxon()
  const {values} = useFormikContext<CreateClassBookingFormType>()
  const {data} = useQuery(ClassQueries.getClassQuery({params: {id: values.classId}}))
  const weekDaysDataToUse = weekDaysData || defaultWeekDays
  const weekDaysSettings = weekDaysNumbers.map((number: number) => {
    const currentDay = startOfSelectedWeek.plus({days: number})
    return {
      date: currentDay,
      disabled: !weekDaysDataToUse[number],
      isToday: currentDay.toFormat('D') === today.toFormat('D'),
      isSelected: currentDay.toFormat('D') === selectedDate.toFormat('D'),
      // text: String(currentDay.toFormat({ weekday: 'narrow' }, { locale: language })),
    }
  })
  const filterDate = useCallback(
    (date: string) => {
      const startOfCurrentDayLuxon = luxon.fromFormat(date, 'dd/MM/yyyy', {zone: 'utc'}).startOf('day')

      if (startOfCurrentDayLuxon.diff(today).milliseconds < 0) {
        return false
      }
      const isWeekDayDisabled = weekDaysSettings.find((el) => el.date.toFormat('dd/MM/yyyy') === date)?.disabled
      if (isWeekDayDisabled) {
        return false
      }
      if (!data?.howFarCanBookUnlimited) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const maxDayTimeStamp = today.plus({day: data?.howFarCanBookAmount}).toMillis()
        return maxDayTimeStamp - startOfCurrentDayLuxon.toMillis() >= 0
      }
      return true
    },
    [startOfSelectedWeek.toFormat('dd/MM/yyyy'), weekDaysDataToUse, selectedDate, data],
  )

  return useMemo(
    () => (
      <InlineCalendar
        onSlideChange={setStartOfSelectedWeek}
        filterDate={filterDate}
        selectedDate={selectedDate.toFormat('dd/MM/yyyy')}
        onDayClick={(date) => setSelectedDate(luxon.fromFormat(date, 'dd/MM/yyyy', {zone: 'utc'}))}
      />
    ),
    [selectedDate, filterDate],
  )
}

type Params = {
  venueId: string
}

export const SelectClassSessionPage: FC = () => {
  const dispatch = useDispatch()
  const luxon = useLuxon()
  const {l} = useLocale()
  const {setIsLoading} = useBookingLoader()
  const {openModal, closeModal} = useModalActions()
  const navigate = useNavigate()
  const {venueId} = useParams<Params>()
  const {setValues: setBookingState, values: bookingState} = useFormikContext<CreateClassBookingFormType>()
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const waitingListBookings = useSelector(UserService.selectors.waitingListBookings())
  const {data: venueData} = useVenue()
  const {data: member, isLoading: isMemberLoading} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {
        venueId: venueData?.uid,
        userId: user?.uid,
      },
    }),
  )
  const now = luxon.now().toUTC().startOf('day')
  const nowWithVenueTz = luxon.fromFormat(
    luxon
      .now()
      .setZone(venueData?.timeZone)
      .toFormat('dd/MM/yyyy HH:mm'),
    'dd/MM/yyyy HH:mm',
    {zone: 'utc'},
  )
  const [selectedDate, setSelectedDate] = useState(now)
  const [startOfSelectedWeek, setStartOfSelectedWeek] = useState(selectedDate.startOf('week'))
  const [verifiedClassIds, setVerifiedClassIds] = useState<string[]>([])
  const handleSetStartOfSelectedWeek = useCallback(
    (slides: Slides) => {
      setStartOfSelectedWeek(slides[0].day)
    },
    [startOfSelectedWeek],
  )
  const handleSelectDate = useCallback((date: DateTime) => {
    setSelectedDate(date)
  }, [])
  const {data: weekDaysData} = useQuery(
    BookingService.queries.getAvailableDaysByWeekQuery({
      query: {
        venueId: venueId || '',
        startOfWeek: luxon
          .fromFormat(startOfSelectedWeek.toFormat('DD', {locale: 'en'}), 'DD', {zone: 'utc', locale: 'en'})
          .toMillis(),
        classesIds: [bookingState.classId],
      },
    }),
  )
  const {isLoading, data} = useQuery(
    BookingService.queries.getVenueClassesSessionsByDateQuery({
      query: {
        date: selectedDate.toMillis(),
        // eslint-disable-next-line
        venueId: venueId!,
        memberId: member?.uid,
        // eslint-disable-next-line
        classesIds: [bookingState.classId!],
      },
    }),
  )

  useEffect(() => {
    if (data?.closestSessionTimeStamp && typeof data.closestSessionTimeStamp === 'number') {
      const dateTime = DateTime.fromMillis(data.closestSessionTimeStamp).toUTC().startOf('day')
      if (dateTime.isValid) {
        setSelectedDate(dateTime)
      }
    }
  }, [data])

  useEffect(() => {
    if (venueId && member?.uid) {
      dispatch(
        getWaitingListBookingsRequest({
          venueId,
          memberId: member.uid,
          l,
        }),
      )
    }
  }, [venueId, member?.uid, dispatch, l])

  return (
    <GridLayout
      withoutPadding
      header={<Header />}
    >
      <ScrollableWrapper>
        <Box py={'16px'}>
          <Box px={'16px'}>
            <WeekCalendar
              today={now}
              selectedDate={selectedDate}
              setSelectedDate={handleSelectDate}
              setStartOfSelectedWeek={handleSetStartOfSelectedWeek}
              weekDaysData={weekDaysData}
              startOfSelectedWeek={startOfSelectedWeek}
            />
          </Box>
          <Box py={'8px'}>
            <DividerWithContent content={selectedDate.toFormat('ccc, MMM d, yyyy')} />
          </Box>
          <Box px={'16px'}>
            {isLoading ? (
              <SessionCard isLoading={true} />
            ) : (
              (Array.isArray(data) ? data : []).map((session, index) => {
                const {
                  price,
                  private: isSessionPrivate,
                  duration,
                  timestamp,
                  className,
                  instructors,
                  participants = [],
                  availableSlots,
                  permissionData: {validated, reason},
                  isSessionAlreadyBooked,
                } = session
                const isPast = nowWithVenueTz.toMillis() > timestamp
                const handleToggleSession = () => {
                  const existing = bookingState.selectedSessions.find(
                    (s) => s.sessionId === session.uid,
                  )
                  if (existing) {
                    setBookingState({
                      ...bookingState,
                      selectedSessions: bookingState.selectedSessions.filter(
                        (s) => s.sessionId !== session.uid,
                      ),
                    })
                  } else {
                    const newSession: SelectedSession = {
                      sessionId: session.uid,
                      timestamp,
                      classId: session.classId,
                      className: (className as string) || '',
                      ...sessionWallClock(timestamp),
                      duration,
                      areaId: session.areaId || '',
                      instructors: instructors || [],
                      maxParticipants: availableSlots,
                      sportType: bookingState.sportType || '',
                      rawSession: session as unknown as Record<string, unknown>,
                    }
                    setBookingState({
                      ...bookingState,
                      // Keep selected sessions sorted earliest-first regardless of the order
                      // they were tapped in (e.g. later session before earlier one). WBA-4102.
                      selectedSessions: [...bookingState.selectedSessions, newSession].sort(
                        (a, b) => (a.timestamp || 0) - (b.timestamp || 0),
                      ),
                    })
                  }
                }
                const isSelected = bookingState.selectedSessions.some(
                  (s) => s.sessionId === session.uid,
                )
                const handleCheckPassCode = (code: string) => {
                  setIsLoading(true)
                  dispatch(
                    checkClassPasscode({
                      code,
                      // eslint-disable-next-line
                      classId: session.classId,
                      callback: () => {
                        closeModal()
                        setVerifiedClassIds((ids) =>
                          ids.includes(session.classId) ? ids : [...ids, session.classId],
                        )
                        handleToggleSession()
                        showMessage(l('success'), 'success')
                        setIsLoading(false)
                      },
                      rejectCallback: () => {
                        setIsLoading(false)
                        showMessage(l('error'), 'error')
                      },
                    }),
                  )
                }
                const isFull = availableSlots <= 0
                const isWaitingListAllowed = session.waitingListAllowed

                const waitingBooking =
                  Array.isArray(waitingListBookings) &&
                  waitingListBookings.find(
                    (b) => b.classId === session.classId && String(b.date_timestamp) === String(session.timestamp),
                  )

                const variant =
                  isFull && isWaitingListAllowed
                    ? waitingBooking
                      ? 'waitingListBooked'
                      : validated
                      ? 'waitingListAvailable'
                      : 'default'
                    : 'default'

                const isDisabled =
                  isPast ||
                  (isFull && !isWaitingListAllowed) ||
                  (!validated && variant !== 'waitingListBooked')

                const handleSessionClick = () => {
                  if (isDisabled) {
                    return
                  }
                  if (variant === 'waitingListAvailable') {
                    openModal({
                      title: l('waitingList'),
                      footerConfig: {
                        disabled: true,
                      },
                      component: (
                        <WaitingListModal
                          session={session}
                          venueId={venueId || ''}
                          userId={user?.uid || ''}
                          userName={user?.name || ''}
                          memberId={member?.uid || ''}
                          variant={variant}
                          isMemberLoading={isMemberLoading}
                          isLoading={isLoading}
                          isPast={isPast}
                          venueData={venueData}
                        />
                      ),
                    })
                    return
                  }

                  if (variant === 'waitingListBooked') {
                    openModal({
                      title: l('waitingList'),
                      footerConfig: {
                        disabled: true,
                      },
                      component: (
                        <UnjoinWaitingListModal
                          session={session}
                          memberId={member?.uid || ''}
                          waitingBookingId={waitingBooking?.uid}
                          venueData={venueData}
                          isPast={isPast}
                        />
                      ),
                    })
                    return
                  }

                  if (isSessionPrivate && !verifiedClassIds.includes(session.classId)) {
                    openModal({
                      title: l('privateSession'),
                      footerConfig: {
                        disabled: true,
                      },
                      component: <PrivateCodeModal handleCheckCode={handleCheckPassCode} />,
                    })
                    return
                  }

                  handleToggleSession()
                }
                return (
                  <Flex
                    mt='8px'
                    key={`session-${index}`}
                  >
                    <SessionCard
                      time={luxon.fromMillis(timestamp).toUTC().toFormat('HH:mm')}
                      price={price}
                      onClick={handleSessionClick}
                      private={isSessionPrivate}
                      selected={isSelected}
                      currency={venueData?.currency}
                      disabled={isDisabled}
                      variant={variant}
                      className={className}
                      isLoading={isMemberLoading || isLoading}
                      disabledText={isPast ? 'past' : messagesByReason(reason)}
                      availableSpots={availableSlots}
                      instructorName={instructors[0]?.name}
                      instructorImage={instructors[0]?.profileImage?.secure_url ?? NO_USER_PHOTO}
                      isSessionAlreadyBooked={isSessionAlreadyBooked}
                      duration={l('minutes_short', {
                        value: duration,
                      })}
                      participants={
                        (availableSlots > 0 && bookingState.class?.participantsNamesWhenFull) ||
                        (availableSlots <= 0 && bookingState.class?.participantsNames)
                          ? participants
                          : null
                      }
                    />
                  </Flex>
                )
              })
            )}
          </Box>
        </Box>
        {bookingState.selectedSessions.length > 0 && (
          <PositionedBox
            bottom='0'
            position='sticky'
          >
            <Box
              bg='white'
              borderTop='1px solid #e2e8f0'
              borderRadius='16px 16px 24px 24px'
              p='16px'
            >
              <Flex
                justifyContent='space-between'
                alignItems='center'
              >
                <Flex
                  alignItems='center'
                  gap='8px'
                >
                  <Text settings={{fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>
                    {bookingState.selectedSessions.length === 1
                      ? (l('session_selected_one') || '1 session selected')
                      : (l('session_selected_other', {value: bookingState.selectedSessions.length}) || `${bookingState.selectedSessions.length} sessions selected`)}
                  </Text>
                  <Box
                    cursor='pointer'
                    onClick={() => {
                      setBookingState({
                        ...bookingState,
                        selectedSessions: [],
                      })
                    }}
                  >
                    <Text settings={{fontSize: '13px', fontWeight: '500', color: 'primary', textDecoration: 'underline'}}>
                      {l('clear') || 'Clear'}
                    </Text>
                  </Box>
                </Flex>
                <Box
                  cursor='pointer'
                  bg='primary'
                  px='24px'
                  py='10px'
                  borderRadius='24px'
                  onClick={() => {
                    const sessions = bookingState.selectedSessions
                    if (sessions.length === 1) {
                      const s = sessions[0]
                      setBookingState({
                        ...bookingState,
                        duration: s.duration,
                        timestamp: s.timestamp,
                        instructors: s.instructors,
                        sessionId: s.sessionId,
                        areaId: s.areaId,
                        classId: s.classId,
                        maxParticipants: s.maxParticipants,
                        date: s.date,
                      })
                      navigate('../set-participants')
                    } else {
                      navigate('../selected-sessions')
                    }
                  }}
                >
                  <Text settings={{color: 'white', fontWeight: 'bold', fontSize: '14px'}}>
                    {l('next')}
                  </Text>
                </Box>
              </Flex>
            </Box>
          </PositionedBox>
        )}
      </ScrollableWrapper>
    </GridLayout>
  )
}
